import { useState } from 'react';
import ClickablePseudo from '../ClickablePseudo';
import type { CiaAgendaInvitePayload } from '../../types/chat';
import { formatFullDate } from '../../utils/forumDates';

interface CiaAgendaInviteMessageProps {
  invite: CiaAgendaInvitePayload;
  author: string;
  currentPseudo: string;
  showProposer?: boolean;
  onRespond?: (accepted: boolean) => Promise<void>;
  onCancel?: () => Promise<void>;
}

function isInactive(etat: CiaAgendaInvitePayload['etat']): boolean {
  return etat !== 'attente' && etat !== 'ok';
}

export default function CiaAgendaInviteMessage({
  invite,
  author,
  currentPseudo,
  showProposer = true,
  onRespond,
  onCancel,
}: CiaAgendaInviteMessageProps) {
  const [loading, setLoading] = useState(false);
  const current = currentPseudo.toLowerCase();
  const isRecipient = invite.recipientPseudo.toLowerCase() === current;
  const isProposer = invite.proposerPseudo.toLowerCase() === current;
  const isAuthor = author.toLowerCase() === current;
  const inactive = isInactive(invite.etat);

  const canRespond = !inactive && invite.etat === 'attente' && isRecipient && !!onRespond;
  const canCancel =
    !inactive &&
    invite.etat === 'attente' &&
    isProposer &&
    !!onCancel;

  const statusLabel = (() => {
    switch (invite.etat) {
      case 'ok': return 'Rendez-vous accepté';
      case 'refus': return 'Proposition refusée';
      case 'annule_auteur': return 'Annulé par le demandeur';
      case 'annule_destinataire': return 'Annulé par le destinataire';
      case 'annule_bot': return 'Rendez-vous caduc';
      default: return 'En attente de réponse';
    }
  })();

  const run = async (fn: () => Promise<void>) => {
    if (loading) return;
    setLoading(true);
    try {
      await fn();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`game-invite-card cia-agenda-invite-card${inactive ? ' status-cancelled' : ''}`}>
      <div className="game-invite-header">
        <span className="game-invite-icon" aria-hidden="true">🏆</span>
        <div className="game-invite-title-wrap">
          <span className="game-invite-title">Rendez-vous CIA</span>
          <span className="game-invite-subtitle">
            {formatFullDate(invite.rdv)} — {statusLabel}
          </span>
        </div>
      </div>

      {showProposer && (
        <div className="game-invite-author">
          Proposé par{' '}
          <ClickablePseudo pseudo={invite.proposerPseudo}>
            {invite.proposerDisplay}
          </ClickablePseudo>
          {' '}à{' '}
          <ClickablePseudo pseudo={invite.recipientPseudo}>
            {invite.recipientDisplay}
          </ClickablePseudo>
        </div>
      )}

      {(canRespond || canCancel) && (
        <div className="game-invite-actions">
          {canRespond && (
            <>
              <button
                type="button"
                className="game-invite-btn accept"
                disabled={loading}
                onClick={() => run(() => onRespond!(true))}
              >
                Accepter
              </button>
              <button
                type="button"
                className="game-invite-btn decline"
                disabled={loading}
                onClick={() => run(() => onRespond!(false))}
              >
                Refuser
              </button>
            </>
          )}
          {canCancel && (
            <button
              type="button"
              className="game-invite-btn cancel"
              disabled={loading}
              onClick={() => run(() => onCancel!())}
            >
              Annuler
            </button>
          )}
        </div>
      )}

      {!canRespond && !canCancel && !inactive && invite.etat === 'ok' && (isProposer || isRecipient) && onCancel && (
        <div className="game-invite-actions">
          <button
            type="button"
            className="game-invite-btn cancel"
            disabled={loading}
            onClick={() => run(() => onCancel())}
          >
            Annuler le RDV
          </button>
        </div>
      )}

      {!isAuthor && invite.etat === 'attente' && isRecipient && (
        <p className="cia-agenda-invite-hint">
          Vous pouvez aussi répondre depuis la page CIA, onglet Composition.
        </p>
      )}
    </div>
  );
}
